// src/pages/BalanceTransactionsTab.js
import React, { useState, useEffect, useMemo } from 'react';
import { fmtDateTimeUS } from '../../utils/dates';

export default function BalanceTransactionsTab() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        const resp = await fetch('/api/balance-transactions/all/', {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!resp.ok) throw new Error('Error loading transactions');
        const data = await resp.json();
        setTransactions(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, [token]);

  const filteredTransactions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return transactions;
    return transactions.filter((tx) =>
      String(tx.company_name || '').toLowerCase().includes(q)
    );
  }, [transactions, query]);

  const downloadReport = async () => {
    try {
      const resp = await fetch('/api/balance-transactions/export/', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!resp.ok) throw new Error(`Error downloading: ${resp.statusText}`);
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'all_balance_transactions.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow overflow-x-auto">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <h3 className="text-2xl font-semibold">All Balance Transactions</h3>

        <div className="flex gap-2 w-full sm:w-auto">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by company..."
            className="flex-1 sm:w-64 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
          />
          <button
            onClick={downloadReport}
            className="bg-blue-600 whitespace-nowrap text-white px-4 py-2 rounded hover:bg-blue-700 transition text-sm"
          >
            Download Report
          </button>
        </div>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="border-b bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left">ID</th>
              <th className="px-4 py-2 text-left">User</th>
              <th className="px-4 py-2 text-left">Company</th>
              <th className="px-4 py-2 text-right">Amount</th>
              <th className="px-4 py-2 text-left">Created At</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.map((tx) => (
              <tr key={tx.id} className="border-b hover:bg-gray-100">
                <td className="px-4 py-2">{tx.id}</td>
                <td className="px-4 py-2">{tx.user_email}</td>
                <td className="px-4 py-2">{tx.company_name}</td>
                <td className="px-4 py-2 text-right">
                  ${parseFloat(tx.amount).toFixed(2)}
                </td>
                <td className="px-4 py-2">
                  {fmtDateTimeUS(tx.created_at)}
                </td>
              </tr>
            ))}

            {filteredTransactions.length === 0 && (
              <tr>
                <td colSpan={5} className="py-4 text-center text-gray-500">
                  {query.trim()
                    ? `No transactions for company "${query.trim()}"`
                    : 'No transactions'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
